import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { getTitleTreeByNumber } from '../../api/title';
import { NodeTypeName, TitleTree } from '../../domain/domain';
import { ApplicationState } from '../../reducers';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import { UiActionType } from '../../reducers/ui.reducer';
import FullScreenProgress from '../FullScreenProgress/FullScreenProgress';
import TitleTreeView from './TitleTreeView';
import { expandNode, findNode, findSubTreeByNodeId, updateURL } from './utils';

type RouteParams = {
    title: string,
    article: string,
    section: string,
    subsection: string,
    paragraph: string,
    subparagraph: string,
    subSubparagraph: string
}

type Props = {
    selectedNodeId: number | undefined,
    onSelectedNodeChanged: (newNodeId: number) => void,
    onApproveSubTree: (titleTree: TitleTree) => void,
    onRejectSubTree: (titleTree: TitleTree) => void
}

const TreePanel: React.FC<Props> = ({ selectedNodeId, onSelectedNodeChanged, onApproveSubTree, onRejectSubTree }) => {
    const [expandedNodes, setExpandedNodes] = useState<number[]>([]);
    const treeRoot = useSelector((app: ApplicationState) => app.comp.treeRoot);
    const [loading, setLoading] = useState<boolean>(false);
    const history = useHistory();
    const match = useRouteMatch<RouteParams>();
    const location = useLocation();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!treeRoot) {
            return;
        }

        // First check if there's a node ID in the query parameters
        const nodeIdParameter = qs.parse(location.search, { ignoreQueryPrefix: true }).node;

        if (nodeIdParameter) {
            // Direct node identification by ID
            const nodeId = parseInt(nodeIdParameter as string);
            const foundNode = findSubTreeByNodeId(treeRoot, nodeId);

            // Node found by ID, update selection and expand path and skip hierarchical traversal
            if (foundNode) {
                onSelectedNodeChanged(foundNode.nodeId);
                setExpandedNodes(expandNode(treeRoot, foundNode.nodeId));
                return;
            }
        }

        const nodeTypes = [
            { param: 'title' as keyof RouteParams, type: NodeTypeName.TITLE },
            { param: 'article' as keyof RouteParams, type: NodeTypeName.ARTICLE },
            { param: 'section' as keyof RouteParams, type: NodeTypeName.SECTION },
            { param: 'subsection' as keyof RouteParams, type: NodeTypeName.SUBSECTION },
            { param: 'paragraph' as keyof RouteParams, type: NodeTypeName.PARAGRAPH },
            { param: 'subparagraph' as keyof RouteParams, type: NodeTypeName.SUBPARAGRAPH },
            { param: 'subSubparagraph' as keyof RouteParams, type: NodeTypeName.SUB_SUBPARAGRAPH }
        ];

        // Search for the deepest node in the hierarchy
        let currentNode = treeRoot;
        let lastFoundNode = undefined;

        // Traverse the hierarchy (title→article→section→...), stopping at the first missing node
        for (const { param, type } of nodeTypes) {
            if (match.params[param] && currentNode) {
                const foundNode = findNode(currentNode, type, match.params[param]);
                if (foundNode) {
                    lastFoundNode = foundNode;
                    currentNode = foundNode;
                } else {
                    break;
                }
            }
        }

        // Update state if we found a node to expand
        let nodeToExpand = lastFoundNode;
        if (nodeToExpand) {
            onSelectedNodeChanged(nodeToExpand.nodeId);
            setExpandedNodes(expandNode(treeRoot, nodeToExpand.nodeId));
        }

    }, [treeRoot?.nodeId, match.params, location.search, treeRoot])

    useEffect(() => {
        if (match.params.title) {
            setLoading(true);
            getTitleTreeByNumber(match.params.title).then(response => {
                dispatch({ type: ComponentManagementActionType.TREE_ROOT_UPDATED, treeRoot: response.data });
            }).catch(error => {
                console.error(error);
                dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Title " + match.params.title + " not found.", severity: "error" } });
            }).finally(() => {
                setLoading(false);
            })
        } else {
            history.push("/component/1");
        }
    }, [match.params.title, dispatch, history]);

    if (treeRoot === undefined || loading) {
        return <FullScreenProgress />;
    }

    return (
        <TitleTreeView
            expandedNodes={expandedNodes}
            selectedNodeId={selectedNodeId}
            root={treeRoot}
            onNodeToggle={(nodeIds: number[]) => {
                if (nodeIds.length < expandedNodes.length) {
                    setExpandedNodes([...nodeIds]);
                } else if (selectedNodeId) {
                    if (nodeIds.findIndex(nodeId => selectedNodeId === nodeId) >= 0) {
                        setExpandedNodes([...nodeIds]);
                    }
                }
            }}
            onApproveSubTreeClicked={onApproveSubTree}
            onRejectSubTreeClicked={onRejectSubTree}
            onNodeClicked={(subTree: TitleTree) => {
                updateURL(treeRoot, subTree, history);
            }}
        />
    )
}

export default TreePanel;